(() => {
    if (window.HDShippingCountdown?.owner === 'hd-shipping-countdown') {
        window.HDShippingCountdown.init();
        return;
    }

    const cardSelector = '[data-hd-card-shipping]';
    const cardLabelSelector = '[data-hd-card-shipping-label]';
    const productSelector = '[data-hd-shipping-countdown]';
    const productLabelSelector = '[data-hd-shipping-title]';
    const timeZone = 'Europe/Istanbul';
    const defaultWorkingDays = [1, 2, 3, 4, 5];
    const turkishWeekdays = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const configuredClosedDates = Array.isArray(window.HDShippingClosedDates)
        ? window.HDShippingClosedDates.filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
        : [];

    let minuteTimeout;
    let minuteInterval;
    let mutationFrame;
    let observer;
    let initialized = false;

    const getIstanbulParts = (date) => {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23'
        }).formatToParts(date);

        return parts.reduce((result, part) => {
            if (part.type !== 'literal') result[part.type] = Number(part.value);
            return result;
        }, {});
    };

    const toDateKey = (date) => [
        date.getUTCFullYear(),
        String(date.getUTCMonth() + 1).padStart(2, '0'),
        String(date.getUTCDate()).padStart(2, '0')
    ].join('-');

    const parseWorkingDays = (value) => {
        if (Array.isArray(value)) return value;
        return String(value || defaultWorkingDays.join(','))
            .split(',')
            .map(Number)
            .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
    };

    const getConfig = (element) => ({
        cutoffHour: Number(element?.dataset.cutoffHour || 12),
        cutoffMinute: Number(element?.dataset.cutoffMinute || 0),
        workingDays: parseWorkingDays(element?.dataset.workingDays),
        closedDates: configuredClosedDates
    });

    const isShippingDay = (date, workingDays, closedDates) => (
        workingDays.includes(date.getUTCDay()) && !closedDates.includes(toDateKey(date))
    );

    const getState = (date = new Date(), config = {}) => {
        const cutoffHour = Number(config.cutoffHour ?? 12);
        const cutoffMinute = Number(config.cutoffMinute ?? 0);
        const workingDays = parseWorkingDays(config.workingDays);
        const closedDates = Array.isArray(config.closedDates) ? config.closedDates : configuredClosedDates;
        const parts = getIstanbulParts(date);
        const localDate = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
        const localTime = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
        const cutoffTime = Date.UTC(parts.year, parts.month - 1, parts.day, cutoffHour, cutoffMinute, 0);
        const shippingDay = isShippingDay(localDate, workingDays, closedDates);
        const isBeforeCutoff = shippingDay && localTime < cutoffTime;
        const minutesRemaining = isBeforeCutoff
            ? Math.max(1, Math.ceil((cutoffTime - localTime) / 60000))
            : 0;

        const tomorrow = new Date(localDate);
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        const nextShippingDate = new Date(localDate);
        do {
            nextShippingDate.setUTCDate(nextShippingDate.getUTCDate() + 1);
        } while (!isShippingDay(nextShippingDate, workingDays, closedDates));

        return {
            isBeforeCutoff,
            isWorkingDay: shippingDay,
            minutesRemaining,
            nextShippingDay: nextShippingDate.getUTCDay(),
            nextShippingDate: toDateKey(nextShippingDate),
            nextIsTomorrow: toDateKey(nextShippingDate) === toDateKey(tomorrow)
        };
    };

    const formatBeforeCutoff = (minutesRemaining) => {
        const hours = Math.floor(minutesRemaining / 60);
        const minutes = minutesRemaining % 60;
        const timeParts = [];

        if (hours > 0) timeParts.push(`${hours} sa`);
        if (minutes > 0 || hours === 0) timeParts.push(`${Math.max(1, minutes)} dk`);

        return `${timeParts.join(' ')} içinde sipariş verirseniz bugün kargoda`;
    };

    const getMessage = (date = new Date(), config = {}) => {
        const state = getState(date, config);
        if (state.isBeforeCutoff) return formatBeforeCutoff(state.minutesRemaining);
        return state.isWorkingDay && state.nextIsTomorrow
            ? 'Yarın kargoda'
            : `${turkishWeekdays[state.nextShippingDay]} kargoda`;
    };

    const queryIncludingRoot = (root, selector) => {
        const elements = [];
        if (root instanceof Element && root.matches(selector)) elements.push(root);
        if (root.querySelectorAll) elements.push(...root.querySelectorAll(selector));
        return elements;
    };

    const render = (root = document, date = new Date()) => {
        queryIncludingRoot(root, cardSelector).forEach((element) => {
            const label = element.querySelector(cardLabelSelector);
            const message = getMessage(date, getConfig(element));
            if (label && label.textContent !== message) label.textContent = message;
        });

        queryIncludingRoot(root, productSelector).forEach((element) => {
            if (element.dataset.variantAvailable !== 'true') {
                element.hidden = true;
                return;
            }

            element.hidden = false;
            const label = element.querySelector(productLabelSelector);
            const message = getMessage(date, getConfig(element));
            if (label && label.textContent !== message) label.textContent = message;
        });
    };

    const scheduleMinuteUpdates = () => {
        window.clearTimeout(minuteTimeout);
        window.clearInterval(minuteInterval);
        const delay = 60000 - (Date.now() % 60000) + 50;

        minuteTimeout = window.setTimeout(() => {
            render();
            minuteInterval = window.setInterval(render, 60000);
        }, delay);
    };

    const updateVariantAvailability = (event) => {
        if (!event.target.matches('product-form input[name="id"]')) return;
        const variants = document.querySelector("variant-radios script[type='application/json'], variant-selects script[type='application/json']");
        if (!variants) return;

        try {
            const variantId = Number(event.target.value);
            const variant = JSON.parse(variants.textContent).find((item) => item.id === variantId);
            if (!variant) return;
            document.querySelectorAll(productSelector).forEach((element) => {
                element.dataset.variantAvailable = String(variant.available);
                render(element);
            });
        } catch (error) {
            console.warn('Hobidabi shipping availability could not be refreshed.', error);
        }
    };

    const init = () => {
        if (initialized) return;
        initialized = true;
        render();
        scheduleMinuteUpdates();
        document.addEventListener('change', updateVariantAvailability);

        observer = new MutationObserver((mutations) => {
            const roots = mutations.flatMap((mutation) => Array.from(mutation.addedNodes))
                .filter((node) => node.nodeType === 1 && (node.matches?.(`${cardSelector}, ${productSelector}`) || node.querySelector?.(`${cardSelector}, ${productSelector}`)));

            if (!roots.length || mutationFrame) return;
            mutationFrame = window.requestAnimationFrame(() => {
                mutationFrame = null;
                roots.forEach((root) => render(root));
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });
    };

    const api = { owner: 'hd-shipping-countdown', getState, formatBeforeCutoff, getMessage, render, init };
    window.HDShippingCountdown = api;
    window.HDCardShippingCountdown = api;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
